import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Card,
  CardHeader,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  CircularProgress,
  Button
} from '@material-ui/core';
import axios from 'axios';
import { useStyles } from './styles';
import moment from 'moment';
import { server_base_url } from '../../context/UserContext';

// We're using the older version of chart.js (2.9.4) that doesn't need registration

const PriceTrend = () => {
  const classes = useStyles();
  const [priceData, setPriceData] = useState({});
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodities, setSelectedCommodities] = useState(['TOMATOES', 'ONIONS', 'POTATOES']);
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('history');
  
  // Fetch available commodities
  useEffect(() => {
    // In a real implementation, you would have an API endpoint to fetch all available commodities
    // For now, we'll use a static list based on common commodities
    const commonCommodities = [
      'TOMATOES', 'ONIONS', 'POTATOES', 'CABBAGE', 'CARROTS', 
      'APPLES', 'BANANAS', 'AVOCADOS', 'GREEN PEPPERS', 'RED PEPPERS',
      'YELLOW PEPPERS', 'BEANS', 'BEETROOT'
    ];
    setCommodities(commonCommodities);
  }, []);
  
  // Fetch price trend data
  useEffect(() => {
    if (selectedCommodities.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    // For historical data, we use the history API with the selected time range
    if (dataSource === 'history') {
      const commodityParam = selectedCommodities.length === 1 ? 
        `commodity=${selectedCommodities[0]}` : '';
      
      const historyUrl = `${server_base_url}/api/market-price-history/?days=${timeRange}${commodityParam ? '&' + commodityParam : ''}`;
      
      axios.get(historyUrl)
        .then(response => {
          const historyData = response.data.time_series || {};
          
          // Filter to only include selected commodities
          const filteredData = {};
          selectedCommodities.forEach(commodity => {
            if (historyData[commodity]) {
              filteredData[commodity] = historyData[commodity];
            }
          });
          
          setPriceData(filteredData);
          setLoading(false);
        })
        .catch(err => {
          setError(`Failed to load historical price data: ${err.message}`);
          setLoading(false);
          console.error(err);
        });
    } else {
      // If not using historical data, use the live or sample data endpoint
      const apiUrl = dataSource === 'live' 
        ? `${server_base_url}/api/live-market_prices/` 
        : `${server_base_url}/api/market_prices/`;
        
      axios.get(apiUrl)
        .then(response => {
          const rawData = response.data;
          // Process the data to extract time series information
          const processedData = processMarketData(rawData, selectedCommodities);
          setPriceData(processedData);
          setLoading(false);
        })
        .catch(err => {
          setError(`Failed to load price data: ${err.message}`);
          setLoading(false);
          console.error(err);
        });
    }
  }, [selectedCommodities, timeRange, dataSource]);
  
  // Process raw market data into time series format
  const processMarketData = (rawData, selectedCommodities) => {
    const processed = {};
    
    // Get the date from the response
    const responseDate = new Date(rawData.date);
    
    // For each product in the data
    rawData.categories.forEach(category => {
      category.products.forEach(product => {
        const commodity = product.Commodity;
        
        // Only process commodities that are selected
        if (selectedCommodities.includes(commodity)) {
          if (!processed[commodity]) {
            processed[commodity] = [];
          }
          
          // Clean the monetary value
          let valueSold = product['Total Value Sold'] || 'R0';
          valueSold = valueSold.replace('R', '').replace(/,/g, '');
          
          let kgSold = product['Total Kg Sold'] || '0';
          kgSold = parseInt(kgSold.replace(/,/g, ''), 10);
          
          // Calculate the price per kg
          const pricePerKg = kgSold > 0 
            ? parseFloat((parseFloat(valueSold) / kgSold).toFixed(2)) 
            : null;
          
          // Add the data point
          processed[commodity].push({
            date: responseDate.toISOString().split('T')[0],
            value_sold: parseFloat(valueSold),
            kg_sold: kgSold,
            price_per_kg: pricePerKg,
            qty_available: parseInt(product['Qty Available'].replace(/,/g, ''), 10)
          });
        }
      });
    });
    
    // For demonstration purposes, simulate historical data over the selected time range
    // In a real app, you would fetch this from your database
    Object.keys(processed).forEach(commodity => {
      const currentData = processed[commodity][0];
      const baseDate = new Date(responseDate);
      const basePricePerKg = currentData.price_per_kg || 10;
      
      // Generate synthetic data for the past days
      for (let i = 1; i <= timeRange; i++) {
        const pastDate = new Date(baseDate);
        pastDate.setDate(pastDate.getDate() - i);
        
        // Create random price fluctuations (±15%)
        const randomFactor = 0.85 + (Math.random() * 0.3);
        const historicalPricePerKg = basePricePerKg * randomFactor;
        
        processed[commodity].push({
          date: pastDate.toISOString().split('T')[0],
          price_per_kg: parseFloat(historicalPricePerKg.toFixed(2)),
          synthetic: true // Flag to indicate this is synthetic data
        });
      }
      
      // Sort by date
      processed[commodity].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    
    return processed;
  };
  
  const handleCommodityChange = (event, commodity) => {
    const newSelected = [...selectedCommodities];
    if (event.target.checked) {
      if (!newSelected.includes(commodity)) {
        newSelected.push(commodity);
      }
    } else {
      const index = newSelected.indexOf(commodity);
      if (index > -1) {
        newSelected.splice(index, 1);
      }
    }
    setSelectedCommodities(newSelected);
  };
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  const handleDataSourceChange = (source) => {
    setDataSource(source);
  };
  
  // Function to get color for each commodity
  const getColor = (index) => {
    const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', 
                   '#FF9F40', '#2E5BFF', '#93BE52', '#FD7272', '#8662E2'];
    return colors[index % colors.length];
  };
  
  // Format dates for labels and organize data for chart
  const formatChartData = () => {
    // Extract all dates from all commodities and sort them
    const allDates = new Set();
    Object.values(priceData).forEach(points => {
      points.forEach(point => allDates.add(point.date));
    });
    
    const sortedDates = Array.from(allDates).sort();
    
    // Format dates for display
    const labels = sortedDates.map(date => moment(date).format('MMM D'));
    
    // Organize datasets for each commodity
    const datasets = Object.entries(priceData).map(([commodity, points], index) => {
      // Create a data point for each date
      const data = sortedDates.map(date => {
        const point = points.find(p => p.date === date);
        return point ? point.price_per_kg : null;
      });
      
      return {
        label: commodity,
        data: data,
        fill: false,
        borderColor: getColor(index),
        backgroundColor: getColor(index),
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1
      };
    });
    
    return { labels, datasets };
  };
  
  // Prepare chart data
  const chartData = formatChartData();
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: false,
          callback: (value) => `R${value}`
        },
        scaleLabel: {
          display: true,
          labelString: 'Price per Kg (R)'
        }
      }],
      xAxes: [{
        scaleLabel: {
          display: true,
          labelString: 'Date'
        }
      }]
    },
    tooltips: {
      callbacks: {
        label: function(tooltipItem, data) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          const value = tooltipItem.yLabel || 0;
          return `${label}: R${value.toFixed(2)} per kg`;
        }
      }
    },
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Market Price Trends'
    }
  };
  
  return (
    <Card className={classes.card}>
      <CardHeader 
        title="Market Price Trends" 
        subheader="Joburg Market Daily Prices" 
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <div className={classes.filters}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              
              <FormControl component="fieldset" className={classes.formControl}>
                <Typography variant="subtitle2">Commodities</Typography>
                <FormGroup>
                  {commodities.map((commodity, index) => (
                    <FormControlLabel
                      key={commodity}
                      control={
                        <Checkbox
                          checked={selectedCommodities.includes(commodity)}
                          onChange={(e) => handleCommodityChange(e, commodity)}
                          name={commodity}
                          style={{ color: getColor(index) }}
                        />
                      }
                      label={commodity}
                    />
                  ))}
                </FormGroup>
              </FormControl>
              
              <FormControl className={classes.formControl}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={14}>Last 14 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 3 months</MenuItem>
                </Select>
              </FormControl>
              
              <div className={classes.dataSourceToggle}>
                <Typography variant="subtitle2" gutterBottom>
                  Data Source
                </Typography>
                <Button
                  variant={dataSource === 'history' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleDataSourceChange('history')}
                  className={classes.sourceButton}
                >
                  Historical
                </Button>
                <Button
                  variant={dataSource === 'live' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleDataSourceChange('live')}
                  className={classes.sourceButton}
                >
                  Live Data
                </Button>
                {/* Sample Data button hidden for presentation
                <Button
                  variant={dataSource === 'sample' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => handleDataSourceChange('sample')}
                  className={classes.sourceButton}
                >
                  Sample Data
                </Button>
                */}
              </div>
            </div>
          </Grid>
          
          <Grid item xs={12} md={9}>
            {loading && (
              <div className={classes.loading}>
                <CircularProgress />
                <Typography variant="body1" style={{ marginTop: 16 }}>
                  Loading price data...
                </Typography>
              </div>
            )}
            
            {error && (
              <div className={classes.error}>
                <Typography color="error">{error}</Typography>
              </div>
            )}
            
            {!loading && !error && Object.keys(priceData).length > 0 && (
              <div className={classes.chartContainer}>
                <Line data={chartData} options={chartOptions} />
                {/* Disclaimer hidden for presentation
                <Typography variant="caption" className={classes.disclaimer}>
                  Note: Historical price data is simulated for demonstration purposes. In a production environment, this would use actual historical data from the database.
                </Typography>
                */}
              </div>
            )}
            
            {!loading && !error && Object.keys(priceData).length === 0 && (
              <div className={classes.noData}>
                <Typography>No price data available for the selected commodities.</Typography>
              </div>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PriceTrend;
